import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import React from 'react';
import { colors } from '../theme/colors';
import { 
  FileText, 
  MapPin, 
  Building, 
  Calculator, 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle, 
  Calendar,
  DollarSign,
  BarChart3,
  Map,
  Shield,
  Target,
  Clock,
  Zap,
  Info,
  Award,
  Briefcase,
  Home,
  Ruler,
  Layers,
  Car
} from 'lucide-react';

// TypeScript interfaces based on the detailed JSON response
export interface PlotInformation {
  gid?: number;
  name?: string;
  fps_code?: string;
  village?: string;
  district?: string;
  state?: string;
  area_sqm?: number;
  area_acres?: number;
  land_use_type?: string | null;
  land_use_purpose?: string | null;
  status?: string;
  tps_id?: number;
}

export interface FSICalculations {
  base_fsi?: number;
  premium_fsi?: number;
  applied_fsi?: number;
  maximum_possible_fsi?: number;
  max_chargeable_fsi?: number;
  gross_buildable_area_sqm?: number;
  maximum_buildable_area_sqm?: number;
  net_developable_area_sqm?: number;
  gross_plot_area_sqm?: number;
  development_efficiency_pct?: number;
  tp_deduction_area_sqm?: number;
  tp_deduction_pct?: number;
}

export interface HeightCompliance {
  building_height_m?: number;
  max_height_allowed_m?: number;
  height_compliant?: boolean;
  height_excess_m?: number;
  consequence_class_code?: string;
  consequence_class_description?: string;
  risk_level?: string;
  seor_requirement?: string;
  cowor_required?: boolean;
  special_approvals_needed?: boolean;
  sla_reference?: string;
}

export interface FinancialEstimates {
  construction?: {
    cost_per_sqm?: number;
    gross_buildable_sqm?: number;
    total_cost?: number;
  };
  infrastructure?: {
    cost_per_sqm?: number;
    total_cost?: number;
  };
  revenue?: {
    sale_rate_per_sqm?: number;
    saleable_area_sqm?: number;
    saleable_ratio?: number;
    total_revenue?: number;
  };
  profitability?: {
    cost_per_saleable_sqm?: number;
    gross_profit?: number;
    margin_percentage?: number;
    roi_percentage?: number;
  };
  statutory_charges?: {
    additional_fsi_charges?: number;
    betterment_charges?: number;
    scrutiny_fees?: number;
    total_statutory?: number;
  };
  misc_costs?: number;
  total_development_cost?: number;
  sla_reference?: string;
}

export interface RoadAnalysis {
  road_width_m?: number;
  road_width_ft?: number;
  road_category?: string;
  premium_percentage?: number;
  premium_multiplier?: number;
  tall_building_eligible?: boolean;
  source?: string;
  sla_reference?: string;
}

export interface LandUseAnalysis {
  land_use_type?: string | null;
  land_use_purpose?: string | null;
  effective_base_fsi?: number;
  fsi_compliant?: boolean;
  fsi_source?: string;
  premium_applicable?: boolean;
  development_charges_applicable?: boolean;
  permitted_fsi_range?: [number, number];
  land_use_compliance?: {
    compliant?: boolean;
    notes?: string[];
  };
  height_restrictions?: {
    max_height_m?: number;
  };
  special_provisions?: string[];
}

export interface OverlaysAnalysis {
  airport_obstacle_zone?: boolean;
  environmental_restriction_zone?: boolean;
  heritage_precinct?: boolean;
  railway_buffer_zone?: boolean;
  river_setback_zone?: boolean;
  overlay_analysis_method?: string;
  recommendations?: string[];
  risk_factors?: string[];
}

export interface RegulatoryRequirements {
  core_approvals_needed?: string[];
  additional_requirements?: string[];
  special_considerations?: string[];
  scrutiny_fees?: {
    rate_per_sqm?: number;
    minimum_fee_rs?: number;
    total_fee_rs?: number;
  };
  timeline_estimates?: {
    development_permission_months?: number;
    building_plan_approval_months?: number;
    construction_phase_months?: number;
    completion_certificate_months?: number;
    total_timeline_months?: number;
  };
  sla_reference?: string;
}

export interface ZoneAnalysis {
  zone_code?: string;
  zone_description?: string;
  base_fsi?: number;
  max_permissible_fsi?: number;
  ground_coverage_pct?: number;
  plot_area_sqm?: number;
  zone_constraints?: string[];
  land_use_override?: boolean;
  has_fallback_zone?: boolean;
  requires_official_verification?: boolean;
}

export interface FlagAndWarning {
  type?: string;
  severity?: string;
  message?: string;
  action?: string;
}

export interface ReportMetadata {
  analysis_version?: string;
  generated_at?: string;
  gid?: number;
  report_type?: string;
  sla_version?: string;
  analysis_parameters?: {
    building_height_m?: number;
    construction_cost_per_sqm?: number;
    sale_rate_per_sqm?: number;
    jantri_rate_per_sqm?: number;
    road_width_m?: number;
    zone_code?: string;
    tp_deduction_pct?: number;
    tall_building_eligible?: boolean;
    include_financial_analysis?: boolean;
    include_tall_building_analysis?: boolean;
    perform_road_analysis?: boolean;
  };
}

export interface RiskAssessment {
  overall_risk_score?: number;
  risk_level?: string;
  risk_assessment_summary?: string;
  risk_categories?: Record<string, number>;
  identified_risks?: Array<{
    category?: string;
    description?: string;
    impact?: string;
    likelihood?: string;
    severity?: string;
  }>;
  mitigation_priority?: string[];
}

export interface ExecutiveSummary {
  investment_recommendation?: {
    rating?: string;
    color?: string;
    confidence_level?: string;
  };
  key_metrics?: {
    applied_fsi?: number;
    buildable_area_sqm?: number;
    projected_profit?: number;
    projected_roi_pct?: number;
    risk_level?: string;
    risk_count?: number;
  };
  critical_factors?: string[];
  next_immediate_actions?: string[];
}

export interface DetailedFSIResponse {
  status: string;
  report_type: string;
  plot_information?: PlotInformation;
  basic_fsi_analysis?: {
    calculation_metadata?: {
      calculated_at?: string;
      calculation_type?: string;
      gid?: number;
      sla_version?: string;
      spatial_road_analysis_used?: boolean;
    };
    fp_information?: PlotInformation;
    fsi_calculations?: FSICalculations;
    height_compliance?: HeightCompliance;
    financial_estimates?: FinancialEstimates;
    road_analysis?: RoadAnalysis;
    land_use_analysis?: LandUseAnalysis;
    overlays_analysis?: OverlaysAnalysis;
    regulatory_requirements?: RegulatoryRequirements;
    zone_analysis?: ZoneAnalysis;
    flags_and_warnings?: FlagAndWarning[];
    recommendations?: string[];
    status?: string;
    chargeable_fsi_analysis?: any; // Add this property to match FSI service
  };
  detailed_sections?: any; // Add this to match FSI service
  executive_summary?: ExecutiveSummary;
  risk_assessment?: RiskAssessment;
  report_metadata?: ReportMetadata;
  recommendations: Array<{
    category: string;
    priority: string;
    actions: string[];
  }>;
}

class ProfessionalPDFExporter {
  private doc: jsPDF;
  private currentY: number = 20;
  private pageWidth: number;
  private pageHeight: number;
  private margin: number = 20;
  private lineHeight: number = 6;
  private sectionSpacing: number = 10;
  
  constructor() {
    this.doc = new jsPDF('p', 'mm', 'a4');
    this.pageWidth = this.doc.internal.pageSize.width;
    this.pageHeight = this.doc.internal.pageSize.height;
  }

  // Utility method to render Lucide React icons to canvas
  private async renderIcon(iconComponent: React.ComponentType<any>, size: number = 20): Promise<string> {
    // Create a temporary div to render the icon
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '-9999px';
    tempDiv.style.width = `${size}px`;
    tempDiv.style.height = `${size}px`;
    tempDiv.style.display = 'flex';
    tempDiv.style.alignItems = 'center';
    tempDiv.style.justifyContent = 'center';
    tempDiv.style.backgroundColor = 'white';
    
    document.body.appendChild(tempDiv);
    
    try {
      // Render the React icon component
      const React = require('react');
      const ReactDOM = require('react-dom');
      
      await new Promise<void>((resolve) => {
        ReactDOM.render(
          React.createElement(iconComponent, { 
            size: size - 4, 
            color: colors.primary,
            strokeWidth: 2
          }),
          tempDiv,
          resolve
        );
      });
      
      // Convert to canvas
      const canvas = await html2canvas(tempDiv, {
        width: size,
        height: size,
        backgroundColor: 'white'
      });
      
      return canvas.toDataURL('image/png');
    } finally {
      document.body.removeChild(tempDiv);
    }
  }

  private checkPageBreak(additionalHeight: number = 0): void {
    if (this.currentY + additionalHeight > this.pageHeight - this.margin) {
      this.doc.addPage();
      this.currentY = this.margin;
    }
  }

  private addHeader(): void {
    // Company logo/header area
    this.doc.setFillColor(colors.primary);
    this.doc.rect(0, 0, this.pageWidth, 25, 'F');
    
    // Title
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(18);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('FSI Analysis Report', this.margin, 15);
    
    // Subtitle
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text('Comprehensive Development Feasibility Report', this.margin, 20);
    
    this.currentY = 35;
  }

  private addFooter(pageNum: number, totalPages: number): void {
    const footerY = this.pageHeight - 10;
    
    // Footer line
    this.doc.setDrawColor(colors.primary);
    this.doc.setLineWidth(0.5);
    this.doc.line(this.margin, footerY - 5, this.pageWidth - this.margin, footerY - 5);
    
    // Page number
    this.doc.setTextColor(colors.neutral);
    this.doc.setFontSize(8);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(`Page ${pageNum} of ${totalPages}`, this.pageWidth - this.margin - 20, footerY);
    
    // Generated timestamp
    const timestamp = new Date().toLocaleDateString('en-IN');
    this.doc.text(`Generated on ${timestamp}`, this.margin, footerY);
  }

  private addSectionHeader(title: string, icon?: React.ComponentType<any>): void {
    this.checkPageBreak(15);
    
    this.doc.setFillColor(247, 247, 247);
    this.doc.rect(this.margin, this.currentY - 2, this.pageWidth - 2 * this.margin, 12, 'F');
    
    this.doc.setTextColor(colors.primary);
    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    
    let textX = this.margin + 3;
    if (icon) {
      // Reserve space for icon
      textX += 8;
    }
    
    this.doc.text(title, textX, this.currentY + 6);
    this.currentY += 15;
  }

  private addKeyValuePair(key: string, value: any, indent: number = 0): void {
    this.checkPageBreak(this.lineHeight);
    
    const x = this.margin + indent;
    const valueStr = this.formatValue(value);
    
    this.doc.setTextColor(colors.black);
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(`${key}:`, x, this.currentY);
    
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(colors.neutral);
    this.doc.text(valueStr, x + 40, this.currentY);
    
    this.currentY += this.lineHeight;
  }

  private addBulletPoint(text: string, indent: number = 0): void {
    this.checkPageBreak(this.lineHeight);
    
    const x = this.margin + indent;
    
    this.doc.setTextColor(colors.primary);
    this.doc.setFontSize(9);
    this.doc.text('•', x, this.currentY);
    
    this.doc.setTextColor(colors.neutral);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(text, x + 5, this.currentY);
    
    this.currentY += this.lineHeight;
  }

  private formatValue(value: any): string {
    if (value === null || value === undefined) return 'N/A';
    if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : 'None';
    if (typeof value === 'number') {
      if (value > 1000) return value.toLocaleString('en-IN');
      return value.toString();
    }
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    return value.toString();
  }

  private addExecutiveSummary(data: DetailedFSIResponse): void {
    if (!data.executive_summary) return;
    
    this.addSectionHeader('Executive Summary', Award);
    
    const summary = data.executive_summary;
    
    // Investment Recommendation Box
    this.doc.setFillColor(230, 245, 235);
    this.doc.rect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, 20, 'F');
    
    this.doc.setTextColor(colors.primary);
    this.doc.setFontSize(11);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Investment Recommendation', this.margin + 5, this.currentY + 8);
    
    this.doc.setFontSize(14);
    this.doc.text(summary.investment_recommendation?.rating || 'N/A', this.margin + 5, this.currentY + 15);
    
    this.currentY += 25;
    
    // Key Metrics
    if (summary.key_metrics) {
      this.addKeyValuePair('Applied FSI', summary.key_metrics.applied_fsi);
      this.addKeyValuePair('Buildable Area', `${summary.key_metrics.buildable_area_sqm?.toLocaleString() || 'N/A'} sqm`);
      this.addKeyValuePair('Projected Profit', `₹${summary.key_metrics.projected_profit?.toLocaleString('en-IN') || 'N/A'}`);
      this.addKeyValuePair('Projected ROI', `${summary.key_metrics.projected_roi_pct || 'N/A'}%`);
      this.addKeyValuePair('Risk Level', summary.key_metrics.risk_level);
    }
    
    this.currentY += this.sectionSpacing;
  }

  private addPlotInformation(data: DetailedFSIResponse): void {
    this.addSectionHeader('Plot Information', MapPin);
    
    const plot = data.plot_information;
    if (!plot) return;
    
    this.addKeyValuePair('Plot ID (GID)', plot.gid || 'N/A');
    this.addKeyValuePair('Plot Name', plot.name || 'N/A');
    this.addKeyValuePair('FPS Code', plot.fps_code || 'N/A');
    this.addKeyValuePair('Village', plot.village || 'N/A');
    this.addKeyValuePair('District', plot.district || 'N/A');
    this.addKeyValuePair('State', plot.state || 'N/A');
    this.addKeyValuePair('Area', `${plot.area_sqm?.toLocaleString() || 'N/A'} sqm (${plot.area_acres || 'N/A'} acres)`);
    this.addKeyValuePair('Land Use Type', plot.land_use_type || 'Not specified');
    
    this.currentY += this.sectionSpacing;
  }

  private addFSIAnalysis(data: DetailedFSIResponse): void {
    if (!data.basic_fsi_analysis) return;
    
    this.addSectionHeader('FSI & Development Analysis', Calculator);
    
    const fsi = data.basic_fsi_analysis.fsi_calculations;
    const zone = data.basic_fsi_analysis.zone_analysis;
    
    // Zone Information
    if (zone) {
      this.doc.setTextColor(colors.primary);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Zoning Details', this.margin, this.currentY);
      this.currentY += 8;
      
      this.addKeyValuePair('Zone Code', zone.zone_code, 5);
      this.addKeyValuePair('Zone Description', zone.zone_description, 5);
      this.addKeyValuePair('Base FSI', fsi?.base_fsi, 5);
      
      this.currentY += 5;
    }
    
    // FSI Calculations
    if (fsi) {
      this.doc.setTextColor(colors.primary);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('FSI Calculations', this.margin, this.currentY);
      this.currentY += 8;
      
      this.addKeyValuePair('Applied FSI', fsi.applied_fsi, 5);
      this.addKeyValuePair('Premium FSI', fsi.premium_fsi, 5);
      this.addKeyValuePair('Maximum Possible FSI', fsi.maximum_possible_fsi, 5);
      this.addKeyValuePair('Gross Buildable Area', `${fsi.gross_buildable_area_sqm?.toLocaleString() || 'N/A'} sqm`, 5);
      this.addKeyValuePair('Maximum Buildable Area', `${fsi.maximum_buildable_area_sqm?.toLocaleString() || 'N/A'} sqm`, 5);
      this.addKeyValuePair('Development Efficiency', `${fsi.development_efficiency_pct || 'N/A'}%`, 5);
    }
    
    this.currentY += this.sectionSpacing;
  }

  private addHeightCompliance(data: DetailedFSIResponse): void {
    const height = data.basic_fsi_analysis?.height_compliance;
    if (!height) return;
    
    this.addSectionHeader('Height & Risk Compliance', Shield);
    
    this.addKeyValuePair('Building Height', `${height.building_height_m || 'N/A'}m`);
    this.addKeyValuePair('Max Allowed Height', `${height.max_height_allowed_m || 'N/A'}m`);
    this.addKeyValuePair('Height Compliant', height.height_compliant);
    this.addKeyValuePair('Consequence Class', `${height.consequence_class_code || 'N/A'} - ${height.consequence_class_description || 'N/A'}`);
    this.addKeyValuePair('Risk Level', height.risk_level);
    this.addKeyValuePair('SEOR Requirement', height.seor_requirement);
    this.addKeyValuePair('COWOR Required', height.cowor_required);
    
    if (height.special_approvals_needed) {
      this.addKeyValuePair('Special Approvals', 'Required');
    }
    
    this.currentY += this.sectionSpacing;
  }

  private addFinancialAnalysis(data: DetailedFSIResponse): void {
    const financial = data.basic_fsi_analysis?.financial_estimates;
    if (!financial) return;
    
    this.addSectionHeader('Financial Analysis', DollarSign);
    
    // Revenue Section
    if (financial.revenue) {
      this.doc.setTextColor(colors.primary);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Revenue Analysis', this.margin, this.currentY);
      this.currentY += 8;
      
      this.addKeyValuePair('Sale Rate per sqm', `₹${financial.revenue.sale_rate_per_sqm?.toLocaleString('en-IN') || 'N/A'}`, 5);
      this.addKeyValuePair('Saleable Area', `${financial.revenue.saleable_area_sqm?.toLocaleString() || 'N/A'} sqm`, 5);
      this.addKeyValuePair('Saleable Ratio', `${financial.revenue.saleable_ratio ? (financial.revenue.saleable_ratio * 100) : 'N/A'}%`, 5);
      this.addKeyValuePair('Total Revenue', `₹${financial.revenue.total_revenue?.toLocaleString('en-IN') || 'N/A'}`, 5);
      
      this.currentY += 5;
    }
    
    // Cost Section
    if (financial.construction || financial.infrastructure || financial.statutory_charges) {
      this.doc.setTextColor(colors.primary);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Cost Analysis', this.margin, this.currentY);
      this.currentY += 8;
      
      this.addKeyValuePair('Construction Cost', `₹${financial.construction?.total_cost?.toLocaleString('en-IN') || 'N/A'}`, 5);
      this.addKeyValuePair('Infrastructure Cost', `₹${financial.infrastructure?.total_cost?.toLocaleString('en-IN') || 'N/A'}`, 5);
      this.addKeyValuePair('Statutory Charges', `₹${financial.statutory_charges?.total_statutory?.toLocaleString('en-IN') || 'N/A'}`, 5);
      this.addKeyValuePair('Miscellaneous Costs', `₹${financial.misc_costs?.toLocaleString('en-IN') || 'N/A'}`, 5);
      this.addKeyValuePair('Total Development Cost', `₹${financial.total_development_cost?.toLocaleString('en-IN') || 'N/A'}`, 5);
      
      this.currentY += 5;
    }
    
    // Profitability Section
    if (financial.profitability) {
      this.doc.setTextColor(colors.primary);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Profitability Analysis', this.margin, this.currentY);
      this.currentY += 8;
      
      this.addKeyValuePair('Gross Profit', `₹${financial.profitability.gross_profit?.toLocaleString('en-IN') || 'N/A'}`, 5);
      this.addKeyValuePair('Profit Margin', `${financial.profitability.margin_percentage || 'N/A'}%`, 5);
      this.addKeyValuePair('ROI', `${financial.profitability.roi_percentage || 'N/A'}%`, 5);
      this.addKeyValuePair('Cost per Saleable sqm', `₹${financial.profitability.cost_per_saleable_sqm?.toLocaleString('en-IN') || 'N/A'}`, 5);
    }
    
    this.currentY += this.sectionSpacing;
  }

  private addRoadAnalysis(data: DetailedFSIResponse): void {
    const road = data.basic_fsi_analysis?.road_analysis;
    if (!road) return;
    
    this.addSectionHeader('Road Analysis', Car);
    
    this.addKeyValuePair('Road Width', `${road.road_width_m?.toFixed(2) || 'N/A'}m (${road.road_width_ft?.toFixed(1) || 'N/A'}ft)`);
    this.addKeyValuePair('Premium Multiplier', `${road.premium_multiplier || 'N/A'}`);
    this.addKeyValuePair('Road Category', road.road_category || 'N/A');
    this.addKeyValuePair('Premium Percentage', `${road.premium_percentage || 'N/A'}%`);
    this.addKeyValuePair('Tall Building Eligible', road.tall_building_eligible ? 'Yes' : 'No');
    
    if (road.source) {
      this.addKeyValuePair('Source', road.source);
    }
    
    this.currentY += this.sectionSpacing;
  }

  private addLandUseAnalysis(data: DetailedFSIResponse): void {
    const landUse = data.basic_fsi_analysis?.land_use_analysis;
    if (!landUse) return;
    
    this.addSectionHeader('Land Use Analysis', Building);
    
    this.addKeyValuePair('Land Use Type', landUse.land_use_type || 'N/A');
    this.addKeyValuePair('Effective Base FSI', `${landUse.effective_base_fsi || 'N/A'}`);
    this.addKeyValuePair('FSI Compliant', landUse.fsi_compliant ? 'Yes' : 'No');
    this.addKeyValuePair('FSI Source', landUse.fsi_source || 'N/A');
    this.addKeyValuePair('Premium Applicable', landUse.premium_applicable ? 'Yes' : 'No');
    this.addKeyValuePair('Development Charges', landUse.development_charges_applicable ? 'Yes' : 'No');
    
    if (landUse.permitted_fsi_range && landUse.permitted_fsi_range.length >= 2) {
      this.addKeyValuePair('Permitted FSI Range', `${landUse.permitted_fsi_range[0]} - ${landUse.permitted_fsi_range[1]}`);
    }
    
    if (landUse.land_use_compliance) {
      this.addKeyValuePair('Land Use Compliance', landUse.land_use_compliance.compliant ? 'Compliant' : 'Non-compliant');
    }
    
    if (landUse.special_provisions && landUse.special_provisions.length > 0) {
      this.currentY += 5;
      this.doc.setTextColor(colors.primary);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Special Provisions:', this.margin, this.currentY);
      this.currentY += 6;
      
      landUse.special_provisions.forEach(provision => {
        this.addBulletPoint(provision, 5);
      });
    }
    
    this.currentY += this.sectionSpacing;
  }

  private addOverlaysAnalysis(data: DetailedFSIResponse): void {
    const overlays = data.basic_fsi_analysis?.overlays_analysis;
    if (!overlays) return;
    
    this.addSectionHeader('Overlay Analysis', Layers);
    
    this.addKeyValuePair('Airport Obstacle Zone', overlays.airport_obstacle_zone ? 'Yes' : 'No');
    this.addKeyValuePair('Environmental Restriction', overlays.environmental_restriction_zone ? 'Yes' : 'No');
    this.addKeyValuePair('Heritage Precinct', overlays.heritage_precinct ? 'Yes' : 'No');
    this.addKeyValuePair('Railway Buffer Zone', overlays.railway_buffer_zone ? 'Yes' : 'No');
    this.addKeyValuePair('River Setback Zone', overlays.river_setback_zone ? 'Yes' : 'No');
    this.addKeyValuePair('Analysis Method', overlays.overlay_analysis_method || 'N/A');
    
    if (overlays.recommendations && overlays.recommendations.length > 0) {
      this.currentY += 5;
      this.doc.setTextColor(colors.primary);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Recommendations:', this.margin, this.currentY);
      this.currentY += 6;
      
      overlays.recommendations.forEach(rec => {
        this.addBulletPoint(rec, 5);
      });
    }
    
    this.currentY += this.sectionSpacing;
  }

  private addRegulatoryRequirements(data: DetailedFSIResponse): void {
    const regulatory = data.basic_fsi_analysis?.regulatory_requirements;
    if (!regulatory) return;
    
    this.addSectionHeader('Regulatory Requirements', Briefcase);
    
    // Core Approvals
    if (regulatory.core_approvals_needed && regulatory.core_approvals_needed.length > 0) {
      this.doc.setTextColor(colors.primary);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Core Approvals Required:', this.margin, this.currentY);
      this.currentY += 6;
      
      regulatory.core_approvals_needed.forEach(approval => {
        this.addBulletPoint(approval, 5);
      });
      
      this.currentY += 5;
    }
    
    // Additional Requirements
    if (regulatory.additional_requirements && regulatory.additional_requirements.length > 0) {
      this.doc.setTextColor(colors.primary);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Additional Requirements:', this.margin, this.currentY);
      this.currentY += 6;
      
      regulatory.additional_requirements.forEach(req => {
        this.addBulletPoint(req, 5);
      });
      
      this.currentY += 5;
    }
    
    // Timeline Estimates
    if (regulatory.timeline_estimates) {
      this.doc.setTextColor(colors.primary);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Timeline Estimates:', this.margin, this.currentY);
      this.currentY += 8;
      
      this.addKeyValuePair('Development Permission', `${regulatory.timeline_estimates.development_permission_months || 'N/A'} months`, 5);
      this.addKeyValuePair('Building Plan Approval', `${regulatory.timeline_estimates.building_plan_approval_months || 'N/A'} months`, 5);
      this.addKeyValuePair('Construction Phase', `${regulatory.timeline_estimates.construction_phase_months || 'N/A'} months`, 5);
      this.addKeyValuePair('Completion Certificate', `${regulatory.timeline_estimates.completion_certificate_months || 'N/A'} months`, 5);
      this.addKeyValuePair('Total Timeline', `${regulatory.timeline_estimates.total_timeline_months || 'N/A'} months`, 5);
      
      this.currentY += 5;
    }
    
    // Scrutiny Fees
    if (regulatory.scrutiny_fees) {
      this.doc.setTextColor(colors.primary);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Scrutiny Fees:', this.margin, this.currentY);
      this.currentY += 8;
      
      this.addKeyValuePair('Rate per sqm', `₹${regulatory.scrutiny_fees.rate_per_sqm || 'N/A'}`, 5);
      this.addKeyValuePair('Minimum Fee', `₹${regulatory.scrutiny_fees.minimum_fee_rs?.toLocaleString('en-IN') || 'N/A'}`, 5);
      this.addKeyValuePair('Total Fee', `₹${regulatory.scrutiny_fees.total_fee_rs?.toLocaleString('en-IN') || 'N/A'}`, 5);
    }
    
    this.currentY += this.sectionSpacing;
  }

  private addRiskAssessment(data: DetailedFSIResponse): void {
    const risk = data.risk_assessment;
    if (!risk) return;
    
    this.addSectionHeader('Risk Assessment', AlertTriangle);
    
    // Risk Summary Box
    let riskColor: [number, number, number] = [255, 235, 235]; // Red
    if (risk.risk_level === 'LOW') riskColor = [235, 255, 235]; // Green
    else if (risk.risk_level === 'MEDIUM') riskColor = [255, 245, 235]; // Orange
    
    this.doc.setFillColor(...riskColor);
    this.doc.rect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, 15, 'F');
    
    this.doc.setTextColor(colors.black);
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(`Risk Level: ${risk.risk_level}`, this.margin + 5, this.currentY + 6);
    this.doc.text(`Risk Score: ${risk.overall_risk_score}`, this.margin + 5, this.currentY + 12);
    
    this.currentY += 20;
    
    this.addKeyValuePair('Risk Assessment', risk.risk_assessment_summary);
    
    // Risk Categories
    if (risk.risk_categories && Object.keys(risk.risk_categories).length > 0) {
      this.currentY += 5;
      this.doc.setTextColor(colors.primary);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Risk Categories:', this.margin, this.currentY);
      this.currentY += 6;
      
      Object.entries(risk.risk_categories).forEach(([category, count]) => {
        this.addKeyValuePair(category, `${count}`, 5);
      });
    }
    
    // Mitigation Priorities
    if (risk.mitigation_priority && risk.mitigation_priority.length > 0) {
      this.currentY += 5;
      this.doc.setTextColor(colors.primary);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Mitigation Priorities:', this.margin, this.currentY);
      this.currentY += 6;
      
      risk.mitigation_priority.forEach(priority => {
        this.addBulletPoint(priority, 5);
      });
    }
    
    this.currentY += this.sectionSpacing;
  }

  private addRecommendations(data: DetailedFSIResponse): void {
    this.addSectionHeader('Recommendations', Target);
    
    if (data.recommendations?.length > 0) {
      data.recommendations.forEach(recGroup => {
        this.doc.setTextColor(colors.primary);
        this.doc.setFontSize(10);
        this.doc.setFont('helvetica', 'bold');
        this.doc.text(`${recGroup.category} (${recGroup.priority} Priority):`, this.margin, this.currentY);
        this.currentY += 6;
        
        if (recGroup.actions?.length > 0) {
          recGroup.actions.forEach(action => {
            this.addBulletPoint(action, 5);
          });
        }
        
        this.currentY += 5;
      });
    }
    
    // Basic recommendations from the analysis
    if (data.basic_fsi_analysis?.recommendations && data.basic_fsi_analysis.recommendations.length > 0) {
      this.doc.setTextColor(colors.primary);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('General Recommendations:', this.margin, this.currentY);
      this.currentY += 6;
      
      data.basic_fsi_analysis.recommendations.forEach(rec => {
        this.addBulletPoint(rec, 5);
      });
    }
    
    this.currentY += this.sectionSpacing;
  }

  private addReportMetadata(data: DetailedFSIResponse): void {
    const metadata = data.report_metadata;
    if (!metadata) return;
    
    this.addSectionHeader('Report Metadata', Info);
    
    this.addKeyValuePair('Report Type', metadata.report_type || 'N/A');
    this.addKeyValuePair('Analysis Version', metadata.analysis_version || 'N/A');
    this.addKeyValuePair('SLA Version', metadata.sla_version || 'N/A');
    this.addKeyValuePair('Generated At', metadata.generated_at ? new Date(metadata.generated_at).toLocaleString('en-IN') : 'N/A');
    
    this.currentY += 5;
    
    // Analysis Parameters
    const params = metadata.analysis_parameters;
    if (params) {
      this.doc.setTextColor(colors.primary);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Analysis Parameters:', this.margin, this.currentY);
      this.currentY += 8;
      
      this.addKeyValuePair('Building Height', `${params.building_height_m || 'N/A'}m`, 5);
      this.addKeyValuePair('Construction Cost', `₹${params.construction_cost_per_sqm?.toLocaleString('en-IN') || 'N/A'}/sqm`, 5);
      this.addKeyValuePair('Sale Rate', `₹${params.sale_rate_per_sqm?.toLocaleString('en-IN') || 'N/A'}/sqm`, 5);
      this.addKeyValuePair('Jantri Rate', `₹${params.jantri_rate_per_sqm?.toLocaleString('en-IN') || 'N/A'}/sqm`, 5);
      this.addKeyValuePair('Road Width', `${params.road_width_m?.toFixed(2) || 'N/A'}m`, 5);
      this.addKeyValuePair('Zone Code', params.zone_code || 'N/A', 5);
      this.addKeyValuePair('TP Deduction', `${params.tp_deduction_pct || 'N/A'}%`, 5);
    }
    
    this.currentY += this.sectionSpacing;
  }

  public async generatePDF(data: DetailedFSIResponse): Promise<jsPDF> {
    // Add header to first page
    this.addHeader();
    
    // Generate all sections with null checks
    if (data.executive_summary) this.addExecutiveSummary(data);
    if (data.plot_information) this.addPlotInformation(data);
    if (data.basic_fsi_analysis) this.addFSIAnalysis(data);
    if (data.basic_fsi_analysis?.height_compliance) this.addHeightCompliance(data);
    if (data.basic_fsi_analysis?.financial_estimates) this.addFinancialAnalysis(data);
    if (data.basic_fsi_analysis?.road_analysis) this.addRoadAnalysis(data);
    if (data.basic_fsi_analysis?.land_use_analysis) this.addLandUseAnalysis(data);
    if (data.basic_fsi_analysis?.overlays_analysis) this.addOverlaysAnalysis(data);
    if (data.basic_fsi_analysis?.regulatory_requirements) this.addRegulatoryRequirements(data);
    if (data.risk_assessment) this.addRiskAssessment(data);
    if (data.recommendations || data.basic_fsi_analysis?.recommendations) this.addRecommendations(data);
    if (data.report_metadata) this.addReportMetadata(data);
    
    // Add footers to all pages
    const totalPages = this.doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      this.doc.setPage(i);
      this.addFooter(i, totalPages);
    }
    
    return this.doc;
  }

  public async exportPDF(data: DetailedFSIResponse, filename?: string): Promise<void> {
    const pdf = await this.generatePDF(data);
    const fileName = filename || `FSI_Report_GID_${data.plot_information?.gid || 'Unknown'}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);
  }
}

// Export function for use in components
export const exportToPDF = async (data: DetailedFSIResponse, filename?: string): Promise<void> => {
  const exporter = new ProfessionalPDFExporter();
  await exporter.exportPDF(data, filename);
};

export default ProfessionalPDFExporter;